import React, { Fragment, useEffect, useState } from 'react';
import moment from 'moment';
import {
	Table,
	Descriptions,
	message,
	Button,
	Switch,
	Dropdown,
	Space,
	Menu,
	Input,
	Col,
	Row,
	Modal,
	Tag,
	Divider,
} from 'antd';
import { DownOutlined, PlusOutlined } from '@ant-design/icons';
import { withRouter } from 'react-router-dom';
import styled from 'styled-components';
import OrganizationModal from '../components/OrganizationModal';
import UserModal from '../components/AddUserModal';
import CopyOrganizationModal from '../components/CopyOrganizationModal';
import GlobalMessages from '../components/GlobalMessages';
import {
	createOrganization,
	getOrganizations,
	toggleFeatures,
	editOrganization,
	addUserToOrganization,
	copyOrganization,
	clearDemoData,
} from '../api';
import OrganizationBackups from '../components/OrganizationBackups';

const { Search } = Input;

const FEATURES_MAP: any = {
	staff_certificates_enabled: 'Staff Certificates and Trainings',
	staff_enabled: 'Staff',
	work_order_staff_enabled: 'Work order Staff',
	contacts_enabled: 'Contacts',
	inventory_owners_enabled: 'Inventory Owners',
	tools_enabled: 'Tools',
	utilization_enabled: 'Tech Log',
	warehouse_enabled: 'Warehouse',
	work_entries_enabled: 'Work time log',
	logistics_enabled: 'Logistics',
	invoicing_enabled: 'Invoicing',
	access_panels_enabled: 'Access Panels',
	organization_backups_enabled: 'Organization Backups',
	smart_work_order_import_enabled: 'Smart Work Order Import',
	smart_component_import_enabled: 'Smart Component Import',
	publications_enabled: 'Publications',
};

const REGION_MAP: any = {
	default: 'Default',
	dgca: 'DGCA',
};

const OrganizationsWrapper = styled.div`
	width: 100%;
	height: 100%;
`;

const ActionsWrapper = styled.div`
	width: 100%;
`;

const AdditionalInfoWrapper = styled.div`
	padding: 0px 24px;
`;

const AddButton = styled(Button)`
	margin-bottom: 24px;
`;

const filterOrganizations = (organizations: any, searchWord: string) => {
	if (!searchWord) {
		return organizations;
	}

	return organizations.filter(
		(a: any) => `${a.name}`.toLowerCase().includes(searchWord.toLowerCase()) || `${a.id}`.includes(searchWord)
	);
};

function Organizations(props: any) {
	const [organizations, setOrganizations]: any = useState([]);
	const [loading, setloading]: any = useState(false);
	const [saving, setSaving]: any = useState(false);
	const [searchValue, setSearchValue]: any = useState('');
	const [orgModalVisible, toggleOrgModal]: any = useState(false);
	const [userModalVisible, toggleUserModal]: any = useState(false);
	const [copOrganizationModalVisible, toggleCopyOrganizationModal]: any = useState(false);
	const [selectedOrg, setSelectedOrg]: any = useState({});

	async function fetchOrganizations() {
		setloading(true);
		const resp = await getOrganizations().catch((err) =>
			message.error('Something went wrong, cannto get organizations')
		);

		if (resp && resp.length) {
			setOrganizations(resp);
		}

		setloading(false);
	}

	async function onClearData(organizationId: string) {
		try {
			Modal.confirm({
				title: 'Do really want to clear this organization demo data?',
				content: 'All items with [DEMO] label will be deleted',
				onOk: async () => {
					setSaving(true);
					await clearDemoData({ organization_id: organizationId });
					await fetchOrganizations();
					setSaving(false);
					setSelectedOrg({});
				},
			});
		} catch (err) {
			setSaving(false);
			message.error('Something went wrong, cannot clear data');
		}
	}

	useEffect(() => {
		fetchOrganizations();
	}, []);

	function getMenu(record: any) {
		return (
			<Menu
				items={[
					{
						key: 'edit',
						label: 'Edit',
						onClick: () => {
							setSelectedOrg(record);
							toggleOrgModal(true);
						},
					},
					{
						key: 'addUser',
						label: 'Add user',
						onClick: () => {
							setSelectedOrg(record);
							toggleUserModal(true);
						},
					},
					{
						key: 'copyOrganizationData',
						label: 'Copy demo organization data',
						onClick: () => {
							setSelectedOrg(record);
							toggleCopyOrganizationModal(true);
						},
					},
					{
						key: 'clearDemoData',
						label: 'Clear demo data',
						onClick: () => onClearData(record.id),
					},
				]}
			/>
		);
	}

	const STATUS_MAP: any = {
		trial: 'In Trial',
		paying: 'Paying',
		payment_required: 'Payment required',
		inactive: 'Inactive',
		freeloader: 'Freeloader',
	};

	const columns = [
		{
			title: 'ID',
			dataIndex: 'id',
			key: 'id',
		},
		{
			title: 'Name',
			dataIndex: 'name',
			key: 'name',
		},
		{
			title: 'Billing Status',
			dataIndex: 'status',
			key: 'status',
			render: (text: string, record: any) => (
				<Fragment>
					{record.billing?.status ? (
						<Tag style={{ marginLeft: 8 }}>{STATUS_MAP[record.billing?.status]}</Tag>
					) : null}
				</Fragment>
			),
		},
		{
			title: 'Trial End',
			dataIndex: 'billing.trial_end',
			render: (text: string, { billing }: any) => {
				if (!billing) {
					return '';
				}

				return billing.trial_end ? `${moment(billing.trial_end).format('DD.MM.YYYY')}` : '';
			},
		},
		{
			title: 'Region',
			dataIndex: 'region',
			width: 100,
			key: 'region',
			render: (text: string, record: any) => REGION_MAP[record.region],
		},
		{
			title: 'Demo Data',
			dataIndex: 'has_demo_data',
			width: 120,
			key: 'has_demo_data',
			render: (text: string, record: any) => (record.has_demo_data ? 'Yes' : 'No'),
		},
		{
			title: 'Users',
			dataIndex: 'users_count',
			width: 100,
			key: 'users_count',
		},
		{
			title: 'Max. A/C Count',
			dataIndex: 'max_aircrafts_count',
			width: 100,
			key: 'max_aircraft_count',
		},
		{
			title: 'A/C Count',
			dataIndex: 'aircrafts_count',
			width: 100,
			key: 'aircrafts_count',
		},
		{
			title: '',
			key: 'action',
			width: 150,
			render: (text: string, record: any) => (
				<>
					<Dropdown overlay={getMenu(record)}>
						<a onClick={(ev: any) => ev.preventDefault()}>
							<Space>
								Actions
								<DownOutlined />
							</Space>
						</a>
					</Dropdown>
				</>
			),
		},
	];

	async function handleFeatureToggle(val: boolean, key: string, id: string) {
		try {
			await toggleFeatures({
				organization_id: id,
				[key]: val,
			});

			const newOrganizations = organizations.map((org: any) => {
				if (org.id === id) {
					return { ...org, features: { ...org.features, [key]: val } };
				}
				return org;
			});

			setOrganizations([...newOrganizations]);
		} catch (err) {
			message.error('Something went wrong, cannot update features');
		}
	}

	async function onSaveOrganization(payload: any) {
		try {
			setSaving(true);
			if (payload.id) {
				await editOrganization(payload.id, payload);
			} else {
				await createOrganization(payload);
			}

			await fetchOrganizations();
			setSelectedOrg({});
			toggleOrgModal(false);
			setSaving(false);
		} catch (err) {
			setSaving(false);
			message.error('Something went wrong, cannot save organization');
		}
	}

	async function onAddUser(payload: any) {
		try {
			setSaving(true);
			await addUserToOrganization(payload);

			await fetchOrganizations();
			setSelectedOrg({});
			toggleUserModal(false);
			setSaving(false);
		} catch (err) {
			setSaving(false);
			message.error('Something went wrong, cannot add user');
		}
	}

	async function onCopyOrganization(payload: any) {
		try {
			setSaving(true);
			payload.to_organization_id = selectedOrg.id;
			await copyOrganization(payload);
			await fetchOrganizations();
			setSaving(false);
			toggleCopyOrganizationModal(false);
			setSelectedOrg({});
		} catch (err) {
			setSaving(false);
			message.error('Something went wrong, cannot copy organization');
		}
	}

	const onSearch = (ev: any) => setSearchValue(ev.target.value);

	return (
		<OrganizationsWrapper>
			<ActionsWrapper>
				<AddButton
					type="primary"
					icon={<PlusOutlined />}
					onClick={() => {
						setSelectedOrg({});
						toggleOrgModal(true);
					}}
				>
					Add new organization
				</AddButton>
				<Search
					placeholder="Search organizations"
					allowClear
					onChange={onSearch}
					style={{ width: 200, float: 'right' }}
				/>
			</ActionsWrapper>
			<Table
				id="main"
				expandedRowRender={(record: any) => {
					return (
						<AdditionalInfoWrapper>
							<Row gutter={16}>
								<Col span={8}>
									<Descriptions column={1} title="Features" size="small">
										{Object.keys(record.features).map((key: string) => {
											return FEATURES_MAP[key] ? (
												<Descriptions.Item key={key} label={FEATURES_MAP[key]}>
													<Switch
														onChange={(val) => handleFeatureToggle(val, key, record.id)}
														defaultChecked={record.features[key]}
													/>
												</Descriptions.Item>
											) : null;
										})}
									</Descriptions>
								</Col>
								<Col span={16}>
									<Descriptions column={3} title="Billing" size="small">
										<Descriptions.Item label="Billing Period">
											{record.billing?.billing_period ? record.billing?.billing_period : ''}
										</Descriptions.Item>
										<Descriptions.Item label="Currency">
											{record.billing?.currency ? record.billing?.currency.toUpperCase() : ''}
										</Descriptions.Item>
										<Descriptions.Item label="Base Fee">
											{record.billing?.base_fee ? record.billing?.base_fee : ''}
										</Descriptions.Item>
										<Descriptions.Item label="Aircraft Fee No Engine">
											{record.billing?.per_aircraft_fee_no_engine
												? record.billing?.per_aircraft_fee_no_engine
												: ''}
										</Descriptions.Item>
										<Descriptions.Item label="Aircraft Fee Piston Engine">
											{record.billing?.per_aircraft_fee_piston
												? record.billing?.per_aircraft_fee_piston
												: ''}
										</Descriptions.Item>
										<Descriptions.Item label="Aircraft Fee Turbine Engine">
											{record.billing?.per_aircraft_fee_turbine
												? record.billing?.per_aircraft_fee_turbine
												: ''}
										</Descriptions.Item>
										<Descriptions.Item label="Aircraft Fee Twin Turbine Engine">
											{record.billing?.per_aircraft_fee_twin_turbine
												? record.billing?.per_aircraft_fee_twin_turbine
												: ''}
										</Descriptions.Item>
										<Descriptions.Item label="Aircraft Fee Jet Engine">
											{record.billing?.per_aircraft_fee_jet
												? record.billing?.per_aircraft_fee_jet
												: ''}
										</Descriptions.Item>
									</Descriptions>
									<Divider />

									{record.features?.organization_backups_enabled && (
										<>
											<Descriptions
												column={1}
												title="Organization Backups"
												size="small"
											></Descriptions>
											<OrganizationBackups organization={record} />
											<Divider />
										</>
									)}
									<Descriptions column={1} title="Global messages" size="small"></Descriptions>
									<GlobalMessages organizationId={record.id} />
								</Col>
							</Row>
						</AdditionalInfoWrapper>
					);
				}}
				pagination={{
					size: 'default',
					pageSize: 40,
					showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
				}}
				locale={{
					emptyText: 'No organizations found',
				}}
				style={{ paddingBottom: 24 }}
				loading={loading}
				rowKey={(record: any) => record.id}
				columns={columns}
				dataSource={filterOrganizations(organizations, searchValue)}
			/>
			<OrganizationModal
				visible={orgModalVisible}
				saving={saving}
				selectedOrg={selectedOrg}
				onClose={() => {
					setSelectedOrg({});
					toggleOrgModal(false);
				}}
				onSave={onSaveOrganization}
			/>
			<UserModal
				visible={userModalVisible}
				saving={saving}
				selectedOrg={selectedOrg}
				onClose={() => {
					setSelectedOrg({});
					toggleUserModal(false);
				}}
				onSave={onAddUser}
			/>
			<CopyOrganizationModal
				visible={copOrganizationModalVisible}
				saving={saving}
				onClose={() => {
					setSelectedOrg({});
					toggleCopyOrganizationModal(false);
				}}
				onSave={onCopyOrganization}
			/>
		</OrganizationsWrapper>
	);
}

export default withRouter(Organizations);
